import { useTrigger } from '@atlassian/post-office-context';
import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';

import { type ScreenSpaceFlagsMessageState } from '../api/types';
import { type JSX } from 'react/jsx-runtime';

// Component to track individual trigger states
// Each instance calls useTrigger independently, avoiding rules of hooks violations
export const EpTriggerTracker = ({
	messageInstanceId,
	epMessageId,
	onTriggerChange,
}: {
	messageInstanceId: string;
	epMessageId: string;
	onTriggerChange: (messageInstanceId: string, isActive: boolean) => void;
}) => {
	const { isActive: requestStart } = useTrigger(epMessageId + '-start');
	const { isActive: isStarted } = useTrigger(epMessageId + '-started');
	const { isActive: requestDismiss } = useTrigger(epMessageId + '-dismiss');
	const { isActive: ctaClick } = useTrigger(epMessageId + '-cta-click');
	const isActiveValue = requestStart || isStarted || requestDismiss || ctaClick || false;

	useEffect(() => {
		onTriggerChange(messageInstanceId, isActiveValue);
	}, [messageInstanceId, isActiveValue, onTriggerChange]);

	return null; // This component doesn't render anything
};

// This hook is used to track the state of EP External Message Triggers
// It returns an object with the message instance id as the key and the trigger state as the value
// The trigger state is true if the trigger is active, false otherwise
// The trigger state will only change to true when the trigger is set to active from the product front-end
export const useEpMigrationTriggers = (
	messages: ScreenSpaceFlagsMessageState[] | undefined,
): {
	triggerStates: Record<string, boolean>;
	EpTriggerWrapper: ({ children }: { children: ReactNode }) => JSX.Element;
} => {
	const [triggerStates, setTriggerStates] = useState<Record<string, boolean>>({});

	// Get all ep-migration messages
	const epMessages = useMemo(
		() =>
			messages?.filter(
				(message) => message.messageTemplateId === 'ep-migration-external-messages',
			) || [],
		[messages],
	);

	const handleTriggerChange = useCallback((messageInstanceId: string, isActive: boolean) => {
		setTriggerStates((prev) => {
			// Only update if the value actually changed
			if (prev[messageInstanceId] === isActive) {
				return prev;
			}
			return {
				...prev,
				[messageInstanceId]: isActive,
			};
		});
	}, []);

	const EpTriggerWrapper = useMemo(
		() =>
			({ children }: { children: ReactNode }): JSX.Element => (
				<>
					{epMessages.map((message: ScreenSpaceFlagsMessageState & { epMessageId: string }) => {
						const epMessageId = message.epMessageId;
						return epMessageId && typeof epMessageId === 'string' ? (
							<EpTriggerTracker
								key={message.messageInstanceId}
								messageInstanceId={message.messageInstanceId}
								epMessageId={epMessageId}
								onTriggerChange={handleTriggerChange}
							/>
						) : null;
					})}
					{children}
				</>
			),
		[epMessages, handleTriggerChange],
	);

	return {
		triggerStates,
		EpTriggerWrapper,
	};
};
